/**
 * Supabase Client
 *
 * Singleton client instance for Supabase interactions
 * Based on Inkwell's battle-tested pattern
 *
 * Environment variables required:
 * - VITE_SUPABASE_URL
 * - VITE_SUPABASE_PUBLISHABLE_KEY (sb_publishable_...)
 */

import { createClient } from "@supabase/supabase-js";

import type { Database } from "../types/database.extended";

type SupabaseClient = ReturnType<typeof createClient<Database>>;

// Supabase configuration from environment.
// Trim to remove any accidental whitespace/newlines.
// Vite injects via import.meta.env; Node tsx scripts get undefined there and fall through to process.env.
const importMetaEnv =
  (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
// `process` is undefined in the browser; only Node (tsx scripts) has it. Read it
// behind a typeof guard so that when an import.meta var is absent (e.g. the dev
// .env has the legacy anon key but not the publishable key), the `??` chain does
// NOT fall through to a bare `process` reference and throw "process is not
// defined" at module load — which blanks the whole app in the dev server.
const nodeEnv: Record<string, string | undefined> =
  typeof process !== "undefined" && process.env ? process.env : {};
const supabaseUrl = (
  importMetaEnv.VITE_SUPABASE_URL ??
  nodeEnv.VITE_SUPABASE_URL ??
  ""
).trim();
// The modern publishable key (sb_publishable_...). The legacy anon-JWT fallback
// was removed once the legacy keys were disabled (API-key modernization Stage 3).
const supabasePublishableKey = (
  importMetaEnv.VITE_SUPABASE_PUBLISHABLE_KEY ??
  nodeEnv.VITE_SUPABASE_PUBLISHABLE_KEY ??
  ""
).trim();

// Validate required environment variables
if (!supabaseUrl || !supabasePublishableKey) {
  console.error("Missing Supabase environment variables");
  console.error(
    "Required: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY",
  );
  console.error("Current values:", {
    url: supabaseUrl,
    key: supabasePublishableKey ? "[REDACTED]" : undefined,
  });
  console.error("Please check your .env file");
}

// Singleton client instance
let supabaseInstance: SupabaseClient | null = null;

/**
 * Get or create Supabase client instance
 *
 * Uses singleton pattern to ensure only one client exists
 * Includes error handling for missing environment variables
 */
export function getSupabaseClient(): SupabaseClient {
  if (!supabaseInstance) {
    if (!supabaseUrl || !supabasePublishableKey) {
      throw new Error(
        "Supabase configuration missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY environment variables.",
      );
    }

    supabaseInstance = createClient<Database>(
      supabaseUrl,
      supabasePublishableKey,
      {
        auth: {
          persistSession: typeof window !== "undefined",
          autoRefreshToken: true,
          detectSessionInUrl: typeof window !== "undefined",
          ...(typeof window !== "undefined" && {
            storage: window.localStorage,
            storageKey: "nexus-auth-token",
          }),
        },
        // Realtime settings (can be disabled if not needed)
        realtime: {
          params: {
            eventsPerSecond: 10,
          },
        },
      },
    );
  }

  return supabaseInstance!;
}

/**
 * Primary export - Supabase client instance
 *
 * Usage:
 * import { supabase } from '@/lib/supabaseClient'
 * const { data, error } = await supabase.from('clients').select('*')
 */
export const supabase = getSupabaseClient();

/**
 * Resolved Supabase URL and publishable (anon-privilege) key.
 *
 * Single source of truth for the handful of public, unauthenticated edge-function
 * fetches that can't go through the `supabase` client (they POST to
 * /functions/v1/* with the publishable key as the bearer). Import these instead
 * of reading import.meta.env directly, so the key wiring lives in one place.
 */
export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_PUBLISHABLE_KEY = supabasePublishableKey;

/**
 * Check if Supabase is properly configured
 *
 * Useful for conditional features or error boundaries
 */
export function isSupabaseConfigured(): boolean {
  return !!(supabaseUrl && supabasePublishableKey);
}

/**
 * Type helper for Supabase queries
 *
 * Usage:
 * type Client = Tables<'clients'>
 * type ClientInsert = TablesInsert<'clients'>
 * type ClientUpdate = TablesUpdate<'clients'>
 */
export type Tables<T extends keyof Database["public"]["Tables"]> =
  Database["public"]["Tables"][T]["Row"];

export type TablesInsert<T extends keyof Database["public"]["Tables"]> =
  Database["public"]["Tables"][T]["Insert"];

export type TablesUpdate<T extends keyof Database["public"]["Tables"]> =
  Database["public"]["Tables"][T]["Update"];

/**
 * Type helper for Supabase enums
 */
export type Enums<T extends keyof Database["public"]["Enums"]> =
  Database["public"]["Enums"][T];

/**
 * Error handling helper for Supabase operations
 *
 * Usage:
 * const result = await handleSupabaseError(
 *   supabase.from('clients').select('*')
 * )
 */
export async function handleSupabaseError<T>(
  promise: Promise<{ data: T | null; error: any }>,
): Promise<T> {
  const { data, error } = await promise;

  if (error) {
    console.error("Supabase error:", error);
    throw new Error(error.message || "An unexpected error occurred");
  }

  if (!data) {
    throw new Error("No data returned from Supabase");
  }

  return data;
}

/**
 * Batch operation helper
 *
 * Supabase doesn't have native batch operations, so we chunk and execute in parallel
 *
 * Usage:
 * await batchOperation(donors, 100, async (batch) => {
 *   return supabase.from('donors').insert(batch)
 * })
 */
export async function batchOperation<T>(
  items: T[],
  batchSize: number,
  operation: (batch: T[]) => Promise<any>,
): Promise<void> {
  const batches: T[][] = [];

  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }

  // Execute batches in parallel (Supabase can handle this)
  await Promise.all(batches.map((batch) => operation(batch)));
}

/**
 * Utility: Convert Supabase timestamp to Date object
 */
export function parseSupabaseTimestamp(timestamp: string | null): Date | null {
  return timestamp ? new Date(timestamp) : null;
}

/**
 * Utility: Format Date for Supabase timestamp
 */
export function formatSupabaseTimestamp(date: Date | null): string | null {
  return date ? date.toISOString() : null;
}

export default supabase;
